import {inject, Injectable} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import {BehaviorSubject, Observable} from 'rxjs';
import {CookieUtils} from './CookieUtils';
import { HttpClient } from '@angular/common/http';
import {APP_CONFIG} from "../app.config";

/**
 * Key for JWT in cookie.
 */
const COOKIE_TOKEN_KEY = 'Auth-Token';

/**
 * Key for JWT in local storage.
 */
export const LOCAL_STORAGE_JWT_KEY = 'ucmim-jwt-token';

/**
 * User authentication service.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private jwtHelperService = inject(JwtHelperService);
  private httpClient = inject(HttpClient);

  private readonly appConfig = inject(APP_CONFIG);
  private loggedIn = new BehaviorSubject<boolean>(false);

  /**
   * Return JSON web token read from local storage.
   */
  static getRawToken(): string {
    AuthService.updateLocalStorageWithJwtFromCookieIfPresent();
    return localStorage.getItem(LOCAL_STORAGE_JWT_KEY)!;
  }

  private static updateLocalStorageWithJwtFromCookieIfPresent() {
    // only the server sets this Cookie and we delete it after reading -> assume it's a new one
    const serverToken = CookieUtils.getCookie(COOKIE_TOKEN_KEY);
    if (serverToken) {
      // update JWT in local storage
      localStorage.setItem(LOCAL_STORAGE_JWT_KEY, serverToken);
      // delete JWT from Cookie (it as already stored in local storage by now)
      CookieUtils.deleteCookie(COOKIE_TOKEN_KEY);
    }
  }

  /**
   * Authenticate user.
   *
   * Return true if user was already authenticated or authentication is successful,
   * false otherwise.
   */
  authenticate(): boolean {
    return this.authenticated();
  }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  /**
   * Remove JSON web token from localstorage and update logged flag to false
   */
  logout(): void {
    localStorage.removeItem(LOCAL_STORAGE_JWT_KEY);
    this.loggedIn.next(false);
  }

  isUserAdmin(): boolean {
    const cookie = AuthService.getRawToken();
    const decodedCookie = this.jwtHelperService.decodeToken(cookie);
    return decodedCookie?.roles?.includes('ROLE_ADMIN');
  }

  hasDealerChangePermission(): boolean {
    const cookie = AuthService.getRawToken();
    const decodedCookie = this.jwtHelperService.decodeToken(cookie);
    return decodedCookie?.dealerChange;
  }

  changeDealer() {
    this.logout();
    // trigger logout and let the spring magic happen
    this.httpClient.post<any>(`/logout`, null).subscribe({
      next: () => {
        // redirect to login page with parameter 'max_age' to enforce new authentication
        window.open(this.appConfig.loginUrl + '?max_age=0', '_self');
      },
    });
  }

  inSupportMode(): boolean {
    const cookie = AuthService.getRawToken();
    const decodedCookie = this.jwtHelperService.decodeToken(cookie);
    return decodedCookie?.supportMode;
  }

  /**
   * True if user is authenticated (JWT is available, valid and not expired).
   */
  private authenticated(): boolean {
    const rawToken = AuthService.getRawToken();
    if (!rawToken) {
      // could not read token
      return false;
    }
    const authenticated = !this.jwtHelperService.isTokenExpired(rawToken);
    this.loggedIn.next(authenticated);
    return authenticated;
  }
}
